import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { 
  Truck, 
  DollarSign, 
  Calendar, 
  Percent, 
  BarChart2, 
  PlusCircle, 
  Tag, 
  Briefcase,
  TrendingDown,
  Info
} from 'lucide-react';
import { 
  calculateUteTruckLoan,
  UteTruckLoanInputs,
  getVehicleSizeByPrice,
  RUNNING_COST_ESTIMATES,
  TAX_RATES
} from '../utils/uteTruckLoanCalculator';
import { formatCurrency } from '../utils/stampDutyCalculator';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

// Constants for form selections
const VEHICLE_TYPE_OPTIONS = [
  { value: 'smallUte', label: 'Small Ute (e.g., Ford Ranger XL)' },
  { value: 'mediumUte', label: 'Medium Ute (e.g., Toyota Hilux SR5)' },
  { value: 'largeUte', label: 'Large Ute (e.g., RAM 1500)' },
  { value: 'lightTruck', label: 'Light Truck (e.g., Isuzu NPR)' },
  { value: 'mediumTruck', label: 'Medium Truck (e.g., Hino 500)' },
  { value: 'heavyTruck', label: 'Heavy Truck (e.g., UD Quon)' }
];

const UTILIZATION_OPTIONS = [
  { value: 'private', label: 'Private Use Only' },
  { value: 'business', label: 'Part Business Use (25%)' },
  { value: 'business-full', label: 'Mainly Business (90%)' },
  { value: 'primary-producer', label: 'Primary Producer' }
];

const LOAN_TERM_OPTIONS = [
  { value: '36', label: '3 Years (36 months)' },
  { value: '48', label: '4 Years (48 months)' },
  { value: '60', label: '5 Years (60 months)' },
  { value: '72', label: '6 Years (72 months)' },
  { value: '84', label: '7 Years (84 months)' }
];

// Chart colors
const CHART_COLORS = [
  '#1e88e5', // Blue
  '#43a047', // Green
  '#e53935', // Red
  '#fdd835', // Yellow
  '#8e24aa', // Purple
  '#fb8c00'  // Orange
];

interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

interface RunningCosts {
  fuelAnnual: number;
  maintenanceAnnual: number;
  registrationInsurance: number;
  tires: number;
  totalAnnual: number;
}

interface TaxBenefits {
  instantAssetWriteOff?: number;
  depreciationYearOne: number;
  interestDeduction: number;
  gstCredit?: number;
  runningCostsDeduction: number;
  estimatedTaxSaving: number;
}

interface CalculationResult {
  monthlyPayment: number;
  totalCost: number;
  totalInterest: number;
  comparisonRate: number;
  amortizationSchedule: AmortizationRow[];
  runningCosts?: RunningCosts;
  taxBenefits?: TaxBenefits;
}

// Helper function to generate payment breakdown chart data
const getPaymentBreakdownData = (principal: number, totalInterest: number) => {
  return [
    { name: 'Principal', value: principal, color: CHART_COLORS[0] },
    { name: 'Interest', value: totalInterest, color: CHART_COLORS[1] }
  ];
};

// Helper function to generate amortization chart data
const getAmortizationChartData = (amortizationSchedule: AmortizationRow[], monthsToShow: number = 12) => {
  return amortizationSchedule
    .slice(0, monthsToShow)
    .map(row => ({
      month: row.month,
      Principal: row.principal,
      Interest: row.interest
    }));
};

// Helper function to generate tax benefit data for chart
const getTaxBenefitData = (taxBenefits: TaxBenefits) => {
  const data = [];
  
  if (taxBenefits.instantAssetWriteOff) {
    data.push({
      name: 'Asset Write-Off',
      value: taxBenefits.instantAssetWriteOff,
      color: CHART_COLORS[0]
    });
  } else {
    data.push({
      name: 'Depreciation',
      value: taxBenefits.depreciationYearOne,
      color: CHART_COLORS[0]
    });
  }
  
  data.push(
    {
      name: 'Interest',
      value: taxBenefits.interestDeduction,
      color: CHART_COLORS[1]
    },
    {
      name: 'Running Costs',
      value: taxBenefits.runningCostsDeduction,
      color: CHART_COLORS[2]
    }
  );
  
  if (taxBenefits.gstCredit) {
    data.push({
      name: 'GST Credit',
      value: taxBenefits.gstCredit,
      color: CHART_COLORS[3]
    });
  }
  
  return data;
};

// Calculate running costs based on vehicle type and usage
const calculateRunningCosts = (
  vehiclePrice: number,
  annualKm: number,
  fuelEfficiency: number,
  fuelPrice: number
): RunningCosts => {
  // Estimate annual fuel cost
  const fuelAnnual = (annualKm / 100) * fuelEfficiency * fuelPrice;
  
  // Estimate annual maintenance (roughly 2% of vehicle price for newer vehicles)
  const maintenanceAnnual = vehiclePrice * 0.02;
  
  // Estimate registration and insurance (roughly 3% of vehicle price)
  const registrationInsurance = vehiclePrice * 0.03;
  
  // Estimate tire replacement costs (based on average replacement every 40,000km)
  const tires = (annualKm / 40000) * 1000;
  
  // Total annual running costs
  const totalAnnual = fuelAnnual + maintenanceAnnual + registrationInsurance + tires;
  
  return {
    fuelAnnual,
    maintenanceAnnual,
    registrationInsurance,
    tires,
    totalAnnual
  };
};

// Calculate tax benefits for business use
const calculateTaxBenefits = (
  vehiclePrice: number,
  businessUsePercentage: number,
  useType: string,
  totalInterest: number,
  runningCosts: RunningCosts,
  isNewVehicle: boolean
): TaxBenefits => {
  // If not business use, no tax benefits
  if (!businessUsePercentage || businessUsePercentage === 0) {
    return {
      depreciationYearOne: 0,
      interestDeduction: 0,
      runningCostsDeduction: 0,
      estimatedTaxSaving: 0
    };
  }
  
  const businessPortion = businessUsePercentage / 100;
  const taxRate = TAX_RATES[useType as keyof typeof TAX_RATES] / 100 || 0;
  
  // Check for instant asset write-off eligibility (simplified - would need updating with current rules)
  let instantAssetWriteOff = undefined;
  if (isNewVehicle && vehiclePrice <= 150000) {
    instantAssetWriteOff = vehiclePrice * businessPortion;
  }
  
  // First year depreciation (if no instant asset write-off)
  const depreciationYearOne = instantAssetWriteOff ? 0 : (vehiclePrice * 0.25 * businessPortion);
  
  // Interest deduction
  const interestDeduction = totalInterest * businessPortion;
  
  // GST credit (only applicable for businesses registered for GST)
  const gstCredit = useType !== 'private' ? (vehiclePrice / 11) * businessPortion : undefined;
  
  // Running costs deduction
  const runningCostsDeduction = runningCosts.totalAnnual * businessPortion;
  
  // Estimated tax saving
  const deductibleAmount = (instantAssetWriteOff || depreciationYearOne) + 
                          interestDeduction + 
                          runningCostsDeduction;
  const estimatedTaxSaving = deductibleAmount * taxRate;
  
  return {
    instantAssetWriteOff,
    depreciationYearOne,
    interestDeduction,
    gstCredit,
    runningCostsDeduction,
    estimatedTaxSaving
  };
};

// Enhanced loan calculation function
const calculateLoan = (
  loanAmount: number, 
  interestRate: number, 
  loanTerm: number,
  balloonPayment: number = 0,
  annualKm: number = 15000,
  fuelEfficiency: number = 10,
  fuelPrice: number = 1.8,
  businessUsePercentage: number = 0,
  useType: string = 'private',
  isNewVehicle: boolean = true
): CalculationResult => {
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = loanTerm * 12;
  const loanPrincipal = loanAmount - balloonPayment / Math.pow(1 + monthlyRate, numberOfPayments);
  
  // Calculate monthly payment using the formula for an amortizing loan with balloon
  const monthlyPayment = loanPrincipal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments) / 
                        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  
  // Calculate total cost and interest
  const totalPayments = monthlyPayment * numberOfPayments;
  const totalCost = totalPayments + balloonPayment;
  const totalInterest = totalCost - loanAmount;
  
  // Calculate comparison rate (simplified - real comparison rates include more fees)
  const comparisonRate = ((totalInterest / loanAmount) / loanTerm) * 100;
  
  // Generate amortization schedule
  const amortizationSchedule: AmortizationRow[] = [];
  let balance = loanAmount;
  
  for (let month = 1; month <= numberOfPayments; month++) {
    const interestPayment = balance * monthlyRate;
    let principalPayment = monthlyPayment - interestPayment;
    
    // Handle balloon payment in the last month
    if (month === numberOfPayments && balloonPayment > 0) {
      principalPayment = balance - balloonPayment;
    }
    
    balance -= principalPayment;
    
    // For the last payment, ensure balance is exactly the balloon amount
    if (month === numberOfPayments) {
      balance = balloonPayment;
    }
    
    amortizationSchedule.push({
      month,
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance
    });
  }
  
  // Calculate running costs if applicable
  const runningCosts = calculateRunningCosts(
    loanAmount,
    annualKm,
    fuelEfficiency,
    fuelPrice
  );
  
  // Calculate tax benefits if applicable
  const taxBenefits = calculateTaxBenefits(
    loanAmount,
    businessUsePercentage,
    useType,
    totalInterest,
    runningCosts,
    isNewVehicle
  );
  
  return {
    monthlyPayment,
    totalCost,
    totalInterest,
    comparisonRate,
    amortizationSchedule,
    runningCosts,
    taxBenefits
  };
};

const UteTruckLoanCalculator: React.FC = () => {
  // State for form inputs
  const [vehiclePrice, setVehiclePrice] = useState('50000');
  const [downPayment, setDownPayment] = useState('10000');
  const [tradeInValue, setTradeInValue] = useState('0');
  const [accessories, setAccessories] = useState('5000');
  const [utilizationCategory, setUtilizationCategory] = useState('private');
  const [loanTerm, setLoanTerm] = useState('60');
  const [interestRate, setInterestRate] = useState('6.99');
  const [balloonPayment, setBalloonPayment] = useState('0');
  const [applicationFee, setApplicationFee] = useState('395');
  const [annualFee, setAnnualFee] = useState('120');
  const [isCommercial, setIsCommercial] = useState(false);
  const [isNewVehicle, setIsNewVehicle] = useState(true);
  const [vehicleType, setVehicleType] = useState('mediumUte');
  const [isTruck, setIsTruck] = useState(false);
  
  // State for calculation results
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [showAmortizationSchedule, setShowAmortizationSchedule] = useState(false);

  // Effect to update vehicle type based on price and truck selection
  useEffect(() => {
    if (vehiclePrice) {
      const price = parseFloat(vehiclePrice);
      const newType = getVehicleSizeByPrice(price, isTruck);
      setVehicleType(newType);
    }
  }, [vehiclePrice, isTruck]);

  // Effect to detect truck selection
  useEffect(() => {
    setIsTruck(vehicleType.includes('Truck'));
  }, [vehicleType]);

  // Effect to update commercial flag based on utilization
  useEffect(() => {
    setIsCommercial(utilizationCategory !== 'private');
  }, [utilizationCategory]);

  // Function to calculate loan details
  const calculateLoanDetails = () => {
    // Validate inputs
    if (!vehiclePrice || parseFloat(vehiclePrice) <= 0) {
      alert('Please enter a valid vehicle price.');
      return;
    }

    // Prepare inputs for calculation
    const inputs: UteTruckLoanInputs = {
      vehiclePrice: parseFloat(vehiclePrice),
      downPayment: parseFloat(downPayment) || 0,
      tradeInValue: parseFloat(tradeInValue) || 0,
      accessories: parseFloat(accessories) || 0,
      utilizationCategory: utilizationCategory,
      loanTerm: parseInt(loanTerm),
      interestRate: parseFloat(interestRate),
      balloonPayment: parseFloat(balloonPayment) || 0,
      applicationFee: parseFloat(applicationFee) || 0,
      annualFee: parseFloat(annualFee) || 0,
      isCommercial: isCommercial,
      isNewVehicle: isNewVehicle
    };

    // Calculate and set results
    const result = calculateLoan(
      inputs.vehiclePrice - inputs.downPayment - inputs.tradeInValue,
      inputs.interestRate,
      inputs.loanTerm,
      inputs.balloonPayment,
      inputs.annualKm,
      inputs.fuelEfficiency,
      inputs.fuelPrice,
      inputs.businessUsePercentage,
      inputs.utilizationCategory,
      inputs.isNewVehicle
    );
    setCalculationResult(result);
  };

  return (
    <>
      <SEOHead
        title="Ute & Truck Loan Calculator | Australian Commercial Vehicle Finance"
        description="Calculate ute and truck loan repayments with our Australian commercial vehicle finance calculator. Compare rates, estimate running costs and understand tax benefits for business vehicles."
        keywords="ute loan calculator, truck finance australia, commercial vehicle loan, work vehicle finance calculator, business vehicle tax benefits, ute finance comparison, Hilux finance, Rangers finance"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Ute & Truck Loan Calculator",
              "description": "Calculate repayments for utes, trucks, and commercial vehicles in Australia. Estimate monthly payments, tax benefits, and total costs including running expenses.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Ute & Truck Loan Calculator"
        description="Calculate your Australian ute or truck loan repayments with our specialized calculator. Whether it's for business or personal use, our tool helps you understand monthly payments, potential tax benefits, and the true cost of financing your work vehicle."
        icon={Truck}
        result={calculationResult ? `Monthly Payment: ${formatCurrency(calculationResult.monthlyPayment)}` : undefined}
      >
        <div className="space-y-6">
          {/* Info box */}
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                Work vehicles like utes and trucks may qualify for tax benefits if used for business purposes. 
                This calculator helps estimate loan costs, running expenses, and potential tax advantages based on your usage.
              </p>
            </div>
          </div>
          
          {/* Input Form */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Vehicle Details Section */}
            <div className="md:col-span-2">
              <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">Vehicle & Purchase Details</h3>
            </div>

            {/* Vehicle Type Selection */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                {VEHICLE_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* New or Used */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Age</label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    className="form-radio h-4 w-4 text-blue-600"
                    checked={isNewVehicle} 
                    onChange={() => setIsNewVehicle(true)}
                  />
                  <span className="ml-2">New</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    className="form-radio h-4 w-4 text-blue-600"
                    checked={!isNewVehicle} 
                    onChange={() => setIsNewVehicle(false)}
                  />
                  <span className="ml-2">Used</span>
                </label>
              </div>
            </div>

            {/* Vehicle Price */}
            <Input
              label="Vehicle Purchase Price"
              value={vehiclePrice}
              onChange={setVehiclePrice}
              unit="$"
              placeholder="e.g. 50000"
            />

            {/* Down Payment */}
            <Input
              label="Down Payment"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 10000"
            />

            {/* Trade-In Value */}
            <Input
              label="Trade-In Value"
              value={tradeInValue}
              onChange={setTradeInValue}
              unit="$"
              placeholder="e.g. 0"
            />

            {/* Accessories */}
            <Input
              label="Accessories (Bull Bar, Canopy, etc.)"
              value={accessories}
              onChange={setAccessories}
              unit="$"
              placeholder="e.g. 5000"
            />

            {/* Utilization Category */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Usage</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={utilizationCategory}
                onChange={(e) => setUtilizationCategory(e.target.value)}
              >
                {UTILIZATION_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This affects potential tax benefits and deductions
              </p>
            </div>

            {/* Commercial Registration */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Type</label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="form-checkbox h-4 w-4 text-blue-600"
                    checked={isCommercial} 
                    onChange={(e) => setIsCommercial(e.target.checked)}
                    disabled={utilizationCategory !== 'private'}
                  />
                  <span className="ml-2">Commercial Vehicle Registration</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {utilizationCategory !== 'private' ? 'Auto-selected for business use' : ''}
              </p>
            </div>

            {/* Loan Details Section */}
            <div className="md:col-span-2">
              <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">Loan Details</h3>
            </div>

            {/* Loan Term */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              >
                {LOAN_TERM_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Interest Rate */}
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 6.99"
            />

            {/* Balloon Payment */}
            <Input
              label="Balloon Payment"
              value={balloonPayment}
              onChange={setBalloonPayment}
              unit="$"
              placeholder="e.g. 0"
            />

            {/* Application Fee */}
            <Input
              label="Application Fee"
              value={applicationFee}
              onChange={setApplicationFee}
              unit="$"
              placeholder="e.g. 395"
            />

            {/* Annual Fee */}
            <Input
              label="Annual Fee"
              value={annualFee}
              onChange={setAnnualFee}
              unit="$"
              placeholder="e.g. 120"
            />
          </div>

          {/* Calculate Button */}
          <div className="mt-6">
            <button
              onClick={calculateLoanDetails}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              Calculate Ute/Truck Loan
            </button>
          </div>

          {/* Results Section */}
          {calculationResult && (
            <div className="mt-8">
              {/* Loan Summary */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                <div className="bg-blue-600 text-white px-6 py-4">
                  <h3 className="text-xl font-semibold">Loan Summary</h3>
                </div>
                
                <div className="p-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Monthly Payment</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(calculationResult.monthlyPayment)}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Total Cost of Loan</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(calculationResult.totalCost)}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Total Interest</p>
                      <p className="text-xl font-semibold text-blue-600">{formatCurrency(calculationResult.totalInterest)}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Comparison Rate</p>
                      <p className="text-xl font-semibold text-blue-600">{calculationResult.comparisonRate.toFixed(2)}%</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Loan Amount</p>
                      <p className="text-xl font-semibold text-blue-600">{formatCurrency(calculationResult.loanAmount)}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Total Fees</p>
                      <p className="text-xl font-semibold text-blue-600">{formatCurrency(calculationResult.totalFees)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Estimated Running Costs */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                <div className="bg-emerald-600 text-white px-6 py-4">
                  <h3 className="text-xl font-semibold">Estimated Running Costs</h3>
                </div>
                
                <div className="p-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Approximate annual running costs based on average {vehicleType.replace(/([A-Z])/g, ' $1').toLowerCase()} expenses in Australia. This is a general estimate and actual costs may vary by location, driving habits, and specific vehicle.
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Fuel (Annual)</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency(calculationResult.runningCosts?.fuelAnnual || 0)}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Maintenance (Annual)</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency(calculationResult.runningCosts?.maintenanceAnnual || 0)}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Registration & Insurance</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency(calculationResult.runningCosts?.registrationInsurance || 0)}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Tires (Annual Average)</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency(calculationResult.runningCosts?.tires || 0)}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Total Monthly Running Cost</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency((calculationResult.runningCosts?.totalAnnual || 0) / 12)}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-500 text-sm">Total Annual Running Cost</p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {formatCurrency(calculationResult.runningCosts?.totalAnnual || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tax Benefits (only show if business use) */}
              {calculationResult.taxBenefits && (utilizationCategory !== 'private') && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                  <div className="bg-purple-600 text-white px-6 py-4">
                    <h3 className="text-xl font-semibold">Estimated Tax Benefits</h3>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Potential tax advantages for business use. These are estimates only - please consult your tax accountant for personalized advice.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {utilizationCategory === 'primary-producer' && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-500 text-sm">Primary Producer Instant Asset Write-Off</p>
                          <p className="text-xl font-semibold text-purple-600">
                            {formatCurrency(calculationResult.taxBenefits?.instantAssetWriteOff || 0)}
                          </p>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">First Year Depreciation</p>
                        <p className="text-xl font-semibold text-purple-600">
                          {formatCurrency(calculationResult.taxBenefits?.depreciationYearOne || 0)}
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Interest Deduction (First Year)</p>
                        <p className="text-xl font-semibold text-purple-600">
                          {formatCurrency(calculationResult.taxBenefits?.interestDeduction || 0)}
                        </p>
                      </div>
                      
                      {isCommercial && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-500 text-sm">GST Credit (Initial Purchase)</p>
                          <p className="text-xl font-semibold text-purple-600">
                            {formatCurrency(calculationResult.taxBenefits?.gstCredit || 0)}
                          </p>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Running Costs Deduction (Annual)</p>
                        <p className="text-xl font-semibold text-purple-600">
                          {formatCurrency(calculationResult.taxBenefits?.runningCostsDeduction || 0)}
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Estimated Tax Saving (First Year)</p>
                        <p className="text-xl font-semibold text-purple-600">
                          {formatCurrency(calculationResult.taxBenefits?.estimatedTaxSaving || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          At {TAX_RATES[utilizationCategory as keyof typeof TAX_RATES].toFixed(2)}% marginal tax rate
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Loan Breakdown Chart */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                <div className="bg-gray-800 text-white px-6 py-4">
                  <h3 className="text-xl font-semibold">Loan Breakdown</h3>
                </div>
                
                <div className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-700 mb-4">Payment Distribution</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={getPaymentBreakdownData(calculationResult.loanAmount, calculationResult.totalInterest)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {getPaymentBreakdownData(calculationResult.loanAmount, calculationResult.totalInterest).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value: number) => [formatCurrency(value), '']}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-medium text-gray-700 mb-4">Amortization Over Time</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={getAmortizationChartData(calculationResult.amortizationSchedule)}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis 
                              tickFormatter={(value) => 
                                value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`
                              } 
                            />
                            <Tooltip formatter={(value: number) => [formatCurrency(value), '']} />
                            <Legend />
                            <Bar dataKey="Principal" name="Principal Balance" fill={CHART_COLORS[0]} />
                            <Bar dataKey="Interest" name="Interest Paid" fill={CHART_COLORS[1]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tax Benefits Chart (only show if business use) */}
              {calculationResult.taxBenefits && utilizationCategory !== 'private' && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                  <div className="bg-purple-600 text-white px-6 py-4">
                    <h3 className="text-xl font-semibold">Tax Benefits Visualization</h3>
                  </div>
                  
                  <div className="p-6">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={getTaxBenefitData(calculationResult.taxBenefits)}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis 
                            tickFormatter={(value) => 
                              value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`
                            } 
                          />
                          <Tooltip formatter={(value: number) => [formatCurrency(value), '']} />
                          <Legend />
                          <Bar dataKey="value" name="Amount" fill={CHART_COLORS[4]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Important Note:</span> Tax benefits are estimates only and may vary based on your specific circumstances, changes in tax laws, and usage patterns. Always consult a qualified tax professional for advice specific to your situation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Amortization Schedule */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gray-800 text-white px-6 py-4 flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Amortization Schedule</h3>
                  <button
                    onClick={() => setShowAmortizationSchedule(!showAmortizationSchedule)}
                    className="text-white hover:text-blue-200 underline text-sm"
                  >
                    {showAmortizationSchedule ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
                
                {showAmortizationSchedule && (
                  <div className="p-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {calculationResult.amortizationSchedule.map((row: AmortizationRow, index: number) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 text-sm text-gray-900">{row.month}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(row.payment)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(row.principal)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Calculator>
    </>
  );
};

export default UteTruckLoanCalculator; 